import { Injectable, inject } from '@angular/core';
import { Observable, interval } from 'rxjs';
import { map, catchError, switchMap, startWith } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AppHttpClientApi } from '@helpers/api/http-client-api';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';
import {
  mapMetricsResponse,
  buildMetricsQueryParams,
  buildObservationsQueryParams
} from './observations-query.utils';
import {
  ObservationListItemDTO,
  ReprocessHistoryDTO,
  ObservationsQueryParams,
  UpdateObservationRequest,
  AddHistoryRequest,
  PaginatedObservationsResponse,
  ObservationMetricsResponse,
  ObservationMetricsParams
} from '../api/observations-api.interface';
import { ObservationMetrics } from '@model/observation/observation-metrics.model';

export interface ObservationsPaginatedResult {
  observations: ObservationListItemDTO[];
  pagination: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ObservationsService {
  private readonly httpClient = inject(AppHttpClientApi);
  private readonly errorHandler = inject(HttpErrorHandlerService);

  private readonly BASE_URL = 'observations';

  getObservations(
    params?: ObservationsQueryParams
  ): Observable<ObservationsPaginatedResult> {
    const queryParams = buildObservationsQueryParams(params);

    return this.httpClient
      .getRaw<PaginatedObservationsResponse>(`${this.BASE_URL}${queryParams}`)
      .pipe(
        map((response) => ({
          observations: response.content,
          pagination: {
            page: response.pageableInfo.page,
            size: response.pageableInfo.size,
            total: response.pageableInfo.totalElements,
            totalPages: response.pageableInfo.totalPages
          }
        })),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getObservations', error)
        )
      );
  }

  updateObservationStatus(
    observationId: string,
    statusId: string
  ): Observable<void> {
    const request: UpdateObservationRequest = { statusId };

    return this.httpClient
      .patchRaw<void>(`${this.BASE_URL}/${observationId}`, request)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('updateObservationStatus', error)
        )
      );
  }

  getObservationHistory(
    observationId: string
  ): Observable<ReprocessHistoryDTO[]> {
    return this.httpClient
      .getRaw<
        ReprocessHistoryDTO[]
      >(`${this.BASE_URL}/${observationId}/history`)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getObservationHistory', error)
        )
      );
  }

  addObservationHistory(
    observationId: string,
    observationText: string,
    lgCode?: string
  ): Observable<void> {
    const request: AddHistoryRequest = { observationText, lgCode };

    return this.httpClient
      .postRaw<void>(`${this.BASE_URL}/${observationId}/history`, request)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('addObservationHistory', error)
        )
      );
  }

  exportObservations(params?: ObservationsQueryParams): Observable<Blob> {
    const queryParams = buildObservationsQueryParams(params, true);

    return this.httpClient
      .getBlob(`${this.BASE_URL}/export${queryParams}`)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('exportObservations', error)
        )
      );
  }

  /**
   * Get observation metrics for Preanalítica.
   */
  getMetrics(
    params?: ObservationMetricsParams
  ): Observable<ObservationMetrics> {
    const queryParams = buildMetricsQueryParams(params);

    return this.httpClient
      .getRaw<ObservationMetricsResponse>(
        `${this.BASE_URL}/metrics${queryParams}`
      )
      .pipe(
        map((response) => mapMetricsResponse(response)),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getMetrics', error)
        )
      );
  }

  /**
   * Auto-refreshing stream of observation metrics.
   */
  getMetricsStream(
    params?: ObservationMetricsParams,
    intervalMs = 60000
  ): Observable<ObservationMetrics> {
    return interval(intervalMs).pipe(
      startWith(0),
      switchMap(() => this.getMetrics(params))
    );
  }

}
